/**
 * Event batcher.
 * Accumulates log events and flushes them in batches:
 * - Every 5 seconds (timer-based)
 * - When 10 events have accumulated (threshold-based)
 * PII scrubbing is handled upstream before events reach the batcher.
 */

import type { LogEvent, FlushResult } from '../types.js';
import type { WriterBackend } from './types.js';
import { FallbackBuffer } from './fallback.js';

const DEFAULT_FLUSH_INTERVAL_MS = 5000;
const DEFAULT_FLUSH_THRESHOLD = 10;

export class EventBatcher {
  private queue: LogEvent[] = [];
  private writer: WriterBackend;
  private fallback: FallbackBuffer;
  private flushIntervalMs: number;
  private flushThreshold: number;
  private timer: ReturnType<typeof setInterval> | null = null;
  private flushing = false;

  constructor(
    writer: WriterBackend,
    options?: {
      flushIntervalMs?: number;
      flushThreshold?: number;
    },
  ) {
    this.writer = writer;
    this.fallback = new FallbackBuffer();
    this.flushIntervalMs = options?.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;
    this.flushThreshold = options?.flushThreshold ?? DEFAULT_FLUSH_THRESHOLD;
  }

  /** Start the flush timer */
  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => {
      void this.flush();
    }, this.flushIntervalMs);
  }

  /** Stop the flush timer */
  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /**
   * Add an event to the batch queue.
   * If the queue reaches the threshold, an immediate flush is triggered.
   */
  enqueue(event: LogEvent): void {
    this.queue.push(event);

    if (this.queue.length >= this.flushThreshold) {
      void this.flush();
    }
  }

  /** Get the current queue length */
  get queueLength(): number {
    return this.queue.length;
  }

  /**
   * Flush all queued events to CloudWatch.
   * On failure, events are buffered to localStorage via FallbackBuffer.
   * On success, any previously buffered events are retried.
   */
  async flush(): Promise<FlushResult> {
    if (this.flushing || this.queue.length === 0) {
      return { success: true, eventsWritten: 0, errors: [] };
    }

    this.flushing = true;

    // Take current queue snapshot and clear it
    const events = [...this.queue];
    this.queue = [];

    // Also grab any previously failed events from fallback
    const bufferedEvents = this.fallback.retrieve();
    const allEvents = [...bufferedEvents, ...events];

    try {
      await this.writer.writeBatch(allEvents);
      // Clear fallback on success (buffered events were written)
      this.fallback.clear();
      this.flushing = false;
      return { success: true, eventsWritten: allEvents.length, errors: [] };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown write error';
      // Buffer all events (current + previously buffered) for retry
      this.fallback.store(allEvents);
      this.flushing = false;
      return { success: false, eventsWritten: 0, errors: [message] };
    }
  }

  /**
   * Flush remaining events and stop the timer.
   * Call this during teardown / page unload.
   */
  async shutdown(): Promise<void> {
    this.stop();
    await this.flush();
  }
}
